import {Inject, Injectable, Injector} from '@angular/core';
import {Router} from '@angular/router';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {of, of as observableOf} from 'rxjs';
import {catchError, concatMap, map, switchMap, tap} from 'rxjs/operators';
import {v4 as uuidv4} from 'uuid';

import {AuthService, LocalStorageService, NotificationService, RolService} from '../../services';
import {DOCUMENT} from '@angular/common';

import {
  authAccessTokenFailure,
  authAccessTokenRequest,
  authAccessTokenSuccess,
  authDPIRedirect,
  authLDAPRedirect,
  authLoginFailure,
  authLoginFREERequest,
  authLoginLDAPRequest,
  authLoginRequest,
  authLoginSuccess,
  authLogout,
  authLogoutFailure,
  authLogoutSuccess,
  authPutRoleSuccess,
  authRedirect
} from './auth.actions';

import {AUTH_ACCESS_TOKEN, AUTH_PERSONA_ID, AUTH_STATE, AUTH_TYPE, AuthType} from '../../models';
import {Store} from '@ngrx/store';
import {AppState} from '../core.state';
import {ConfigService} from '../../../shared';
import {Rol} from "../../enums";

@Injectable()
export class AuthEffects {

  envName: string;
  dpiRedirect = createEffect(() =>
      this.actions$.pipe(
        ofType(authDPIRedirect),
        tap(() => {
            this.authRedirect(AuthType.OAUTH_IDP);
          }
        )
      ),
    {dispatch: false}
  );
  ldapRedirect = createEffect(() =>
      this.actions$.pipe(
        ofType(authLDAPRedirect),
        tap(() => {
            this.authRedirect(AuthType.OAUTH_LDAP);
          }
        )
      ),
    {dispatch: false}
  );
  loginRequest = createEffect(() =>
    this.actions$.pipe(
      ofType(authLoginRequest),
      switchMap(payload => {
        const applicationId = ConfigService.get('applicationId');
        return this.authService.login(payload.access_token, payload.authType, applicationId)
          .pipe(map(personaIds => {
              let personaId = '';
              if (personaIds && personaIds.length !== 0) {
                personaId = personaIds.pop();
              }
              return authLoginSuccess({personaId, authType: payload.authType});
            }
            ),
            catchError(error =>
              observableOf(
                authLoginFailure({
                  error
                })
              )
            )
          );
      })
    )
  );
  loginLDAPRequest = createEffect(() =>
    this.actions$.pipe(
      ofType(authLoginLDAPRequest),
      tap(payload => {
        if (payload.personaId) {
          this.localStorageService.setItem(AUTH_PERSONA_ID, payload.personaId);
          this.rolService.saveRole({rolId: Rol.DGRN, rolNombre: 'DGRN'});
        }
        this.router.navigate(['/'])
          .then(() => {
            window.location.reload();
          });
      })
    ),
    {dispatch: false}
  );
  loginFREERequest = createEffect(() =>
    this.actions$.pipe(
      ofType(authLoginFREERequest),
      tap(payload => {
        if (payload.personaId) {
          this.localStorageService.setItem(AUTH_PERSONA_ID, payload.personaId);
          this.rolService.saveRole({rolId: Rol.USUARIO_LIBRE, rolNombre: 'Usuario Libre'});
        }
        this.router.navigate(['/'])
          .then(() => {
            window.location.reload();
          });
      })
    ),
    {dispatch: false}
  );
  accessRequest = createEffect(() =>
    this.actions$.pipe(
      ofType(authAccessTokenRequest),
      switchMap(payload => {
        const applicationId = ConfigService.get('applicationId');
          return this.authService.getAccessToken({code: payload.code, authType: payload.authType, applicationId})
            .pipe(
              map(tokens => authAccessTokenSuccess({tokens, authType: payload.authType})),
              catchError(error =>
                observableOf(
                  authAccessTokenFailure({
                    error: 'Error al obtener el access_token con el code.'
                  })
                )
              )
            );
        }
      )
    )
  );
  accessTokenSuccess = createEffect(() =>
    this.actions$.pipe(
      ofType(authAccessTokenSuccess),
      concatMap(payload => {
        const tokens = payload.tokens;
        this.authService.saveTokens(tokens);
        return of(authLoginRequest({access_token: tokens.access_token, authType: payload.authType}));
      })
    )
  );
  accessTokenFailure = createEffect(() =>
      this.actions$.pipe(
        ofType(authAccessTokenFailure),
        tap(payload => {
          this.authService.removeTokens();
          this.notificationService.error(payload.error);
          this.router.navigate(['/features/login']);
        })
      ),
    {dispatch: false}
  );
  loginSuccess = createEffect(() =>
      this.actions$.pipe(
        ofType(authLoginSuccess),
        tap(payload => {

          let comun = true;
          if(payload.authType){
            if(payload.authType === AuthType.OAUTH_LDAP){
              this.store.dispatch(authLoginLDAPRequest({personaId: payload.personaId}));
              comun = false;
            }
            if(payload.authType === AuthType.OAUTH_FREE){
              this.store.dispatch(authLoginFREERequest({personaId: payload.personaId}));
              comun = false;
            }
          }

          if(comun){
            let routeRedircect = '/features/put-role';

            if (payload.personaId !== '') {
              this.localStorageService.setItem(AUTH_PERSONA_ID, payload.personaId);
            } else {
              routeRedircect = '/features/register-person';
            }

            this.store.dispatch(authRedirect({url: routeRedircect}));
          }
        })
      ),
    {dispatch: false}
  );
  loginFailure = createEffect(() =>
      this.actions$.pipe(
        ofType(authLoginFailure),
        tap(payload => {
          this.localStorageService.removeItem(AUTH_PERSONA_ID);
          this.notificationService.error(payload.error);
        })
      ),
    {dispatch: false}
  );
  putRoleSuccess = createEffect(() =>
      this.actions$.pipe(
        ofType(authPutRoleSuccess),
        tap(payload => {
          this.rolService.saveRole(payload.role);
          // Todo, aqui recargo la pagina para no usar este que es de un modulo inferior
          // puede buscarce otra forma de hacerlo
          //this.loadDataService.load();
          this.router.navigate(['/'])
            .then(() => {
              window.location.reload();
            });
        })
      ),
    {dispatch: false}
  );
  redirect = createEffect(() =>
      this.actions$.pipe(
        ofType(authRedirect),
        tap(payload => {
          this.router.navigate([payload.url]);
        })
      ),
    {dispatch: false}
  );
  logout = createEffect(() =>
      this.actions$.pipe(
        ofType(authLogout),
        tap(() => {
          const token = this.localStorageService.getItem(AUTH_ACCESS_TOKEN);
          const authType = this.localStorageService.getItem(AUTH_TYPE);
          if (token) {
            this.authService.logout();
            const oauthFaker = ConfigService.get('oauth_user_free');

            if (oauthFaker && oauthFaker.access_token === token) {
              this.store.dispatch(authLogoutSuccess({isLoading: false}));
              this.router.navigate(['/features/login']);
            } else {
              const state = uuidv4();
              const oauthConfig = ConfigService.get(`oauth_${authType}`);
              const redirectUri = `${oauthConfig.redirect_base_uri}/login`;
              const tokenParts = token.split('!');
              let repository = '';
              if (tokenParts.length > 0) {
                repository = tokenParts[0];
              }

              const urlLogout = `${oauthConfig.url}/signout?oauth=signout&client_id=${oauthConfig.client_id}&redirect_uri=${redirectUri}&token=${token}&state=${state}&first_call=1&repository=${repository}&server_ip=1`;
              this.document.defaultView.window.open(urlLogout, '_self');
              this.store.dispatch(authLogoutSuccess({isLoading: true}));
            }
          } else {
            this.router.navigate(['/features/login']);
            this.store.dispatch(authLogoutFailure());
          }
        })
      ),
    {dispatch: false}
  );

  authRedirect(authType) {
    const state = uuidv4();
    const localStorageService = this.injector.get(LocalStorageService);
    localStorageService.setItem(AUTH_STATE, state);
    localStorageService.setItem(AUTH_TYPE, authType);
    const oauth = ConfigService.get(`oauth_${authType}`);
    const redirectUri = `${oauth.redirect_base_uri}/oauth/gam/callback`;
    const url = `${oauth.url}/signin?oauth=auth&scope=gam_user_data+gam_user_roles&client_id=${oauth.client_id}&redirect_uri=${redirectUri}&state=${state}`;
    this.document.defaultView.window.open(url, '_self');
  }

  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private rolService: RolService,
    private notificationService: NotificationService,
    private localStorageService: LocalStorageService,
    private router: Router,
    private injector: Injector,
    private store: Store<AppState>,
    @Inject(DOCUMENT) readonly document: Document
  ) {
    this.envName = ConfigService.get('envName');
  }
}
